import { Copy, Download, ShieldCheck } from "lucide-react";
import QRCode from "qrcode";
import { useEffect, useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router";
import { toast } from "sonner";
import { AuthLayout } from "@/components/layout/auth-layout";
import { Button } from "@/components/ui/button";
import { Input, StackField } from "@/components/ui/form";
import { Notice } from "@/components/ui/notice";
import { Spinner } from "@/components/ui/panel";
import { Steps } from "@/components/ui/steps";
import { authClient, authErrorMessage } from "@/lib/auth-client";
import { useMe, useRefreshMe } from "@/lib/session";

/** Mandatory TOTP enrollment on first sign-in (brief §7). */
export function SetupTwoFactorPage() {
  const me = useMe();
  const navigate = useNavigate();
  const refresh = useRefreshMe();
  const [step, setStep] = useState(0);
  const [password, setPassword] = useState("");
  const [totpURI, setTotpURI] = useState("");
  const [qr, setQr] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (totpURI) void QRCode.toDataURL(totpURI, { width: 220, margin: 1 }).then(setQr);
  }, [totpURI]);

  if (me.isPending) return <Spinner />;
  if (!me.data) return <Navigate to="/login" replace />;
  if (me.data.user.twoFactorEnabled && step < 2) return <Navigate to="/" replace />;

  const secret = totpURI ? (new URL(totpURI).searchParams.get("secret") ?? "") : "";

  const start = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const { data, error } = await authClient.twoFactor.enable({ password, method: "totp" });
    setLoading(false);
    if (error || !data || data.method !== "totp") return setError(authErrorMessage(error));
    setTotpURI(data.totpURI);
    setBackupCodes(data.backupCodes);
    setStep(1);
  };

  const verify = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const { error } = await authClient.twoFactor.verifyTotp({ code: code.replace(/\s/g, "") });
    setLoading(false);
    if (error) return setError(authErrorMessage(error));
    setStep(2);
  };

  const finish = async () => {
    await refresh();
    navigate("/", { replace: true });
  };

  const downloadCodes = () => {
    const blob = new Blob([`Expert Column AI バックアップコード\n${me.data!.user.email}\n\n${backupCodes.join("\n")}\n`], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "expert-column-ai-backup-codes.txt";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <AuthLayout title="二段階認証の設定" lead="安全のため、すべての方に二段階認証の設定をお願いしています。">
      <div className="mb-5">
        <Steps steps={["パスワード確認", "アプリに登録", "コードを保存"]} current={step} />
      </div>
      {error && <Notice tone="bad" className="mb-4">{error}</Notice>}

      {step === 0 && (
        <form noValidate onSubmit={start} className="space-y-4">
          <p className="text-sm text-ink-2">Google Authenticator や Microsoft Authenticator などの認証アプリをスマートフォンにご用意ください。</p>
          <StackField label="現在のパスワード">{(id) => <Input id={id} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />}</StackField>
          <Button type="submit" size="lg" className="w-full" loading={loading} disabled={!password}>
            次へ
          </Button>
        </form>
      )}

      {step === 1 && (
        <form noValidate onSubmit={verify} className="space-y-4">
          <p className="text-sm text-ink-2">認証アプリで次の QR コードを読み取り、表示された6桁のコードを入力してください。</p>
          <div className="flex justify-center">{qr ? <img src={qr} alt="二段階認証の登録用 QR コード" width={220} height={220} className="rounded border border-line" /> : <Spinner />}</div>
          <details className="rounded-md border border-line bg-ground px-3 py-2 text-[13px]">
            <summary className="cursor-pointer font-bold">QR コードを読み取れない場合</summary>
            <p className="mt-2">アプリで「キーを入力」を選び、次のキーを入力してください。</p>
            <code className="mt-1 block font-mono text-sm break-all select-all">{secret}</code>
          </details>
          <StackField label="確認コード（6桁）">
            {(id) => <Input id={id} value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" maxLength={7} className="text-center font-mono text-xl" />}
          </StackField>
          <Button type="submit" size="lg" className="w-full" loading={loading} disabled={code.replace(/\s/g, "").length !== 6}>
            <ShieldCheck />
            確認して有効にする
          </Button>
        </form>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <Notice tone="ok" title="二段階認証を有効にしました" />
          <Notice tone="warn" title="バックアップコードを保存してください">
            スマートフォンをなくしたときにログインするためのコードです。各コードは1回だけ使えます。この画面を閉じると、もう表示されません。
          </Notice>
          <ul className="grid grid-cols-1 gap-2 rounded-md border border-line bg-ground p-3 font-mono text-sm min-[360px]:grid-cols-2">
            {backupCodes.map((c) => (
              <li key={c} className="text-center">{c}</li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="flex-1" onClick={() => void navigator.clipboard.writeText(backupCodes.join("\n")).then(() => toast.success("コピーしました"))}>
              <Copy />
              コピー
            </Button>
            <Button variant="outline" className="flex-1" onClick={downloadCodes}>
              <Download />
              ダウンロード
            </Button>
          </div>
          <Button size="lg" className="w-full" onClick={() => void finish()}>
            保存しました。はじめる
          </Button>
        </div>
      )}
    </AuthLayout>
  );
}
